import {
  type AnyValidator,
  createBrowserHistory,
  createRootRouteWithContext,
  createRoute,
  createRouter,
  type ErrorComponentProps,
  type ErrorRouteComponent,
  notFound,
  type ParamsOptions,
  type RouteComponent,
  type RouterHistory,
  redirect,
  SearchParamError,
  useMatches,
  useNavigate,
} from '@tanstack/react-router';
import { type ComponentProps, type ComponentType, Suspense, useEffect } from 'react';
import { type CanonicalRouteSpec, canonicalRouteSpecs } from './routeCatalog';
import { createNotFoundRouteComponent, createRouteComponent, createRouteComponentWithProps } from './routeComponent';
import * as routePages from './routePages';
import {
  loadAiInboxThreads,
  loadBusinessEntity,
  loadBusinessSetupTab,
  loadBuySellOrderCore,
  loadDocumentOnboardingCore,
  loadGrowerProductCore,
  loadGrowerProductsFirstPage,
  loadInventorySettingsTab,
  loadInventoryTransferCore,
  loadInventoryTransfersFirstPage,
  loadInventoryWarehouses,
  loadLotCore,
  loadOrderBoardCards,
  loadPickTicketCore,
  loadPickTicketsFirstPage,
  loadPricingSheet,
  loadProductionRunCore,
  loadProductionRunsFirstPage,
  loadPurchaseOrderCore,
  loadPurchaseOrdersFirstPage,
  loadReceivingTicketCore,
  loadReceivingTicketsFirstPage,
  loadSalesOrderCore,
  loadSalesOrdersFirstPage,
  loadTagLayouts,
  loadTeamForwardingBanner,
  loadWarehouseDetails,
} from './routeQueryLoaders';
import {
  type AppRouterContext,
  getAuthStatus,
  getFrameKey,
  isRouteDeclared,
  PENDING_APP_ROUTER_CONTEXT,
  type RouteAccess,
} from './routerAccess';
import {
  accountingReportsSearchSchema,
  accountRegisterSearchSchema,
  agingReportsSearchSchema,
  aiInboxSearchSchema,
  biDashboardsSearchSchema,
  documentEditorSearchSchema,
  loginSearchSchema,
  palletTagEditorSearchSchema,
  settingsAccountingSearchSchema,
  settingsBusinessSearchSchema,
  settingsEntitiesSearchSchema,
  settingsInventorySearchSchema,
  settingsTeamSearchSchema,
  viewLotTrackingSearchSchema,
} from './routeSearch';

declare module '@tanstack/react-router' {
  interface StaticDataRouteOption {
    access?: RouteAccess;
  }
}

export type { AppRouterContext } from './routerAccess';

const ASCII_INTEGER = /^\d+$/;

type ViewLotTrackingParams = { slug: string; warehouseId: number };
type EntityIdParams = { id: number };
type WarehouseIdParams = { warehouseId: number };

function PropagateRouteError({ error }: ErrorComponentProps): never {
  throw error;
}

const NotFoundRoutePage = createNotFoundRouteComponent(routePages.NotFoundPage);

function RootNotFoundComponent() {
  const { auth } = rootRoute.useRouteContext();
  if (auth.loading) return null;

  return (
    <Suspense fallback={null}>
      <NotFoundRoutePage />
    </Suspense>
  );
}

const rootRoute = createRootRouteWithContext<AppRouterContext>()({ notFoundComponent: RootNotFoundComponent });

const framedRoute = createRoute({
  getParentRoute: () => rootRoute,
  id: 'agriful-frame',
  beforeLoad: ({ context }) => ({ authStatus: getAuthStatus(context) }),
  component: AuthenticatedAgrifulLayout,
});

// Withholds the frame for anyone who is not authenticated. It never navigates:
// RoutesBridge owns the single signed-out redirect for every route, framed or not.
function AuthenticatedAgrifulLayout() {
  const context = framedRoute.useRouteContext();
  const routeAccess = useMatches({ select: (matches) => matches.at(-1)?.staticData.access });

  if (context.authStatus !== 'authenticated') return null;

  const stockChat = context.auth.isMember && context.featureFlags.aiV2 === true;
  const frameKey = getFrameKey(stockChat ? 'member' : (routeAccess ?? 'authenticated'));
  return <routePages.AgrifulLayout key={frameKey} />;
}

function enforceRouteAccess(context: AppRouterContext, access: RouteAccess): void {
  if (!isRouteDeclared(context, access)) {
    throw notFound({ routeId: rootRoute.id });
  }
}

type FramedRouteOptions<TSearchValidator extends AnyValidator> = Readonly<{
  access?: RouteAccess;
  errorComponent?: ErrorRouteComponent;
  validateSearch?: TSearchValidator;
}>;

function createFramedRouteFromComponent<const TPath extends string, TSearchValidator extends AnyValidator = undefined>(
  routeSpec: CanonicalRouteSpec<TPath>,
  component: RouteComponent,
  { access = 'member', errorComponent, validateSearch }: FramedRouteOptions<TSearchValidator> = {}
) {
  return createRoute({
    getParentRoute: () => framedRoute,
    path: routeSpec.path,
    beforeLoad: ({ context }) => enforceRouteAccess(context, access),
    staticData: { access },
    component,
    errorComponent,
    validateSearch,
  });
}

function createFramedRoute<const TPath extends string, TSearchValidator extends AnyValidator = undefined>(
  routeSpec: CanonicalRouteSpec<TPath>,
  component: RouteComponent,
  options: FramedRouteOptions<TSearchValidator> = {}
) {
  return createFramedRouteFromComponent(routeSpec, createRouteComponent(component), options);
}

function createFramedRouteWithProps<
  TProps extends object,
  const TPath extends string,
  TSearchValidator extends AnyValidator = undefined,
>(
  routeSpec: CanonicalRouteSpec<TPath>,
  component: ComponentType<TProps>,
  usePageProps: () => NoInfer<TProps>,
  options: FramedRouteOptions<TSearchValidator> = {}
) {
  return createFramedRouteFromComponent(routeSpec, createRouteComponentWithProps(component, usePageProps), options);
}

const emailSettingsComponent = createRouteComponent(routePages.EmailSettingsPage);

const loginRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: canonicalRouteSpecs.login.path,
  validateSearch: loginSearchSchema,
  component: createRouteComponentWithProps(routePages.LoginPage, useLoginPageProps),
});

const scanDebugRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: canonicalRouteSpecs.scanDebug.path,
  component: createRouteComponent(routePages.ScanDebugPage),
});

/** The search results page is gone (search lives in the ⌘K palette); bookmarks of it land on the app. */
const searchResultsRedirectRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: canonicalRouteSpecs.searchResults.path,
  beforeLoad: () => {
    throw redirect({ to: canonicalRouteSpecs.landing.path });
  },
});
const landingRoute = createFramedRoute(canonicalRouteSpecs.landing, routePages.LandingPage, {
  access: 'authenticated',
});
const notAMemberRoute = createFramedRoute(canonicalRouteSpecs.notAMember, routePages.NotAMemberPage, {
  access: 'authenticated',
});
const purchaseOrdersRoute = createFramedRoute(canonicalRouteSpecs.purchaseOrders, routePages.PurchaseOrdersPage);
purchaseOrdersRoute.updateLoader({ loader: ({ context }) => loadPurchaseOrdersFirstPage(context) });
const purchaseOrderRoute = createFramedRouteWithProps(
  canonicalRouteSpecs.purchaseOrder,
  routePages.PurchaseOrderPage,
  usePurchaseOrderPageProps
);
purchaseOrderRoute.updateLoader({ loader: ({ context, params }) => loadPurchaseOrderCore(context, params.slug) });
const settingsRoute = createFramedRoute(canonicalRouteSpecs.settings, routePages.SettingsPage);
const salesOrdersRoute = createFramedRoute(canonicalRouteSpecs.salesOrders, routePages.SalesOrdersPage);
salesOrdersRoute.updateLoader({ loader: ({ context }) => loadSalesOrdersFirstPage(context) });
const salesOrderRoute = createFramedRouteWithProps(
  canonicalRouteSpecs.salesOrder,
  routePages.SalesOrderPage,
  useSalesOrderPageProps
);
salesOrderRoute.updateLoader({ loader: ({ context, params }) => loadSalesOrderCore(context, params.slug) });
const aiInboxRoute = createFramedRouteWithProps(
  canonicalRouteSpecs.aiInbox,
  routePages.AiInboxPage,
  useAiInboxPageProps,
  { validateSearch: aiInboxSearchSchema }
);
// Opening a thread stays on this route; the mounted list already holds its page, so only an entry preloads.
aiInboxRoute.updateLoader({
  loader: ({ context, cause }) =>
    context.featureFlags.aiV2 || cause === 'stay' ? undefined : loadAiInboxThreads(context),
});
const chatHomeRoute = createRoute({
  getParentRoute: () => framedRoute,
  path: canonicalRouteSpecs.chatHome.path,
  staticData: { access: 'member-ai-v2' },
  beforeLoad: ({ context }) => {
    enforceRouteAccess(context, 'member-ai-v2');
    throw redirect({ to: '/', replace: true });
  },
});
const chatThreadRoute = createFramedRoute(canonicalRouteSpecs.chatThread, routePages.EmptyPage, {
  access: 'member-ai-v2',
});
const buySellOrderRoute = createFramedRouteWithProps(
  canonicalRouteSpecs.buySellOrder,
  routePages.BuySellOrderPage,
  useBuySellOrderPageProps
);
buySellOrderRoute.updateLoader({ loader: ({ context, params }) => loadBuySellOrderCore(context, params.slug) });
const documentOnboardingRoute = createFramedRouteWithProps(
  canonicalRouteSpecs.documentOnboarding,
  routePages.DocumentOnboardingPage,
  useDocumentOnboardingPageProps
);
documentOnboardingRoute.updateLoader({
  loader: ({ context, params }) => loadDocumentOnboardingCore(context, Number(params.id)),
});
const growerProductsRoute = createFramedRoute(canonicalRouteSpecs.growerProducts, routePages.GrowerProductsPage);
growerProductsRoute.updateLoader({ loader: ({ context }) => loadGrowerProductsFirstPage(context) });
const growerProductRoute = createFramedRouteWithProps(
  canonicalRouteSpecs.growerProduct,
  routePages.GrowerProductPage,
  useGrowerProductPageProps
);
growerProductRoute.updateLoader({ loader: ({ context, params }) => loadGrowerProductCore(context, params.slug) });
const accountingReportsRoute = createFramedRouteWithProps(
  canonicalRouteSpecs.accountingReports,
  routePages.AccountingReportsPage,
  useAccountingReportsPageProps,
  { access: 'member-non-dawson', validateSearch: accountingReportsSearchSchema }
);
const accountsRoute = createFramedRoute(canonicalRouteSpecs.accounts, routePages.AccountsPage, {
  access: 'member-non-dawson',
});
const accountRegisterRoute = createFramedRouteWithProps(
  canonicalRouteSpecs.accountRegister,
  routePages.AccountRegisterPage,
  useAccountRegisterPageProps,
  { access: 'member-non-dawson', validateSearch: accountRegisterSearchSchema }
);
const invoicesRoute = createFramedRoute(canonicalRouteSpecs.invoices, routePages.ViewInvoicesPage, {
  access: 'member-non-dawson',
});
const payablesRoute = createFramedRoute(canonicalRouteSpecs.payables, routePages.ViewPayablesPage, {
  access: 'member-non-dawson',
});
const billRoute = createFramedRouteWithProps(canonicalRouteSpecs.bill, routePages.BillPage, useBillPageProps, {
  access: 'member-non-dawson',
});
const paymentsRoute = createFramedRoute(canonicalRouteSpecs.payments, routePages.PaymentsPage, {
  access: 'member-non-dawson',
});
const printChecksRoute = createFramedRoute(canonicalRouteSpecs.printChecks, routePages.PrintChecksPage, {
  access: 'member-non-dawson',
});
const paymentRoute = createFramedRouteWithProps(
  canonicalRouteSpecs.payment,
  routePages.PaymentPage,
  usePaymentPageProps,
  { access: 'member-non-dawson' }
);
const agingReportsRoute = createFramedRouteWithProps(
  canonicalRouteSpecs.agingReports,
  routePages.AgingReportsPage,
  useAgingReportsPageProps,
  { access: 'member-non-dawson', validateSearch: agingReportsSearchSchema }
);
const inventoryRoute = createFramedRoute(canonicalRouteSpecs.inventory, routePages.InventoryPage);
inventoryRoute.updateLoader({ loader: ({ context }) => loadInventoryWarehouses(context) });
const productionRunsRoute = createFramedRoute(canonicalRouteSpecs.productionRuns, routePages.ProductionRunsPage);
productionRunsRoute.updateLoader({ loader: ({ context }) => loadProductionRunsFirstPage(context) });
const productionRunRoute = createFramedRouteWithProps(
  canonicalRouteSpecs.productionRun,
  routePages.ProductionRunPage,
  useProductionRunPageProps
);
productionRunRoute.updateLoader({ loader: ({ context, params }) => loadProductionRunCore(context, params.slug) });
const viewPickTicketsRoute = createFramedRoute(canonicalRouteSpecs.viewPickTickets, routePages.PickTicketsPage);
viewPickTicketsRoute.updateLoader({ loader: ({ context }) => loadPickTicketsFirstPage(context) });
const editPickTicketRoute = createFramedRouteWithProps(
  canonicalRouteSpecs.editPickTicket,
  routePages.PickTicketPage,
  usePickTicketPageProps
);
editPickTicketRoute.updateLoader({ loader: ({ context, params }) => loadPickTicketCore(context, params.slug) });
const viewReceivingTicketsRoute = createFramedRoute(
  canonicalRouteSpecs.viewReceivingTickets,
  routePages.ReceivingTicketsPage
);
viewReceivingTicketsRoute.updateLoader({ loader: ({ context }) => loadReceivingTicketsFirstPage(context) });
const editReceivingTicketRoute = createFramedRouteWithProps(
  canonicalRouteSpecs.editReceivingTicket,
  routePages.ReceivingTicketPage,
  useReceivingTicketPageProps
);
editReceivingTicketRoute.updateLoader({
  loader: ({ context, params }) => loadReceivingTicketCore(context, params.slug),
});

const viewLotTrackingCodec = {
  parse: (params) =>
    ASCII_INTEGER.test(params.warehouseId) ? { ...params, warehouseId: Number(params.warehouseId) } : false,
  stringify: (params) => ({ ...params, warehouseId: String(params.warehouseId) }),
} satisfies NonNullable<
  ParamsOptions<typeof canonicalRouteSpecs.viewLotTracking.path, ViewLotTrackingParams>['params']
>;
const viewLotTrackingRoute = createRoute({
  getParentRoute: () => framedRoute,
  path: canonicalRouteSpecs.viewLotTracking.path,
  beforeLoad: ({ context }) => enforceRouteAccess(context, 'member'),
  params: viewLotTrackingCodec,
  validateSearch: viewLotTrackingSearchSchema,
  loader: ({ context, params }) => loadLotCore(context, params.slug, params.warehouseId),
  staticData: { access: 'member' },
  component: createRouteComponentWithProps(routePages.LotPage, useLotPageProps),
});
const inventoryTransferRoute = createFramedRouteWithProps(
  canonicalRouteSpecs.inventoryTransfer,
  routePages.InventoryTransferPage,
  useInventoryTransferPageProps
);
inventoryTransferRoute.updateLoader({
  loader: ({ context, params }) => loadInventoryTransferCore(context, params.slug),
});
const inventoryTransfersRoute = createFramedRoute(
  canonicalRouteSpecs.inventoryTransfers,
  routePages.InventoryTransfersPage
);
inventoryTransfersRoute.updateLoader({ loader: ({ context }) => loadInventoryTransfersFirstPage(context) });
const settingsAccountingRoute = createFramedRoute(
  canonicalRouteSpecs.settingsAccounting,
  routePages.AccountingSettingsPage,
  { validateSearch: settingsAccountingSearchSchema }
);
const settingsBusinessRoute = createRoute({
  getParentRoute: () => framedRoute,
  path: canonicalRouteSpecs.settingsBusiness.path,
  beforeLoad: ({ context }) => enforceRouteAccess(context, 'member'),
  validateSearch: settingsBusinessSearchSchema,
  loaderDeps: ({ search }) => ({ tab: search.tab }),
  loader: ({ context, deps }) => loadBusinessSetupTab(context, deps.tab),
  staticData: { access: 'member' },
  component: createRouteComponent(routePages.BusinessSetupPage),
});

const entityIdCodec = {
  parse: (params) => (ASCII_INTEGER.test(params.id) ? { id: Number(params.id) } : false),
  stringify: (params) => ({ id: String(params.id) }),
} satisfies NonNullable<ParamsOptions<typeof canonicalRouteSpecs.settingsEntities.path, EntityIdParams>['params']>;
const settingsEntitiesRoute = createRoute({
  getParentRoute: () => framedRoute,
  path: canonicalRouteSpecs.settingsEntities.path,
  beforeLoad: ({ context }) => enforceRouteAccess(context, 'member'),
  params: entityIdCodec,
  validateSearch: settingsEntitiesSearchSchema,
  staticData: { access: 'member' },
  loader: ({ context, params }) => loadBusinessEntity(context, params.id),
  component: createRouteComponentWithProps(routePages.BusinessEntityPage, useSettingsEntitiesPageProps),
});

const pricingSheetRoute = createRoute({
  getParentRoute: () => framedRoute,
  path: canonicalRouteSpecs.pricingSheet.path,
  beforeLoad: ({ context }) => enforceRouteAccess(context, 'member'),
  params: entityIdCodec,
  staticData: { access: 'member' },
  loader: ({ context, params }) => loadPricingSheet(context, params.id),
  component: createRouteComponent(routePages.PricingSheetPage),
});
const settingsAutomationsRoute = createFramedRouteFromComponent(
  canonicalRouteSpecs.settingsAutomations,
  emailSettingsComponent
);
const settingsEmailsRoute = createFramedRouteFromComponent(canonicalRouteSpecs.settingsEmails, emailSettingsComponent);
const settingsOrdersRoute = createFramedRoute(canonicalRouteSpecs.settingsOrders, routePages.OrderSettingsPage);
const settingsDocumentsRoute = createFramedRoute(
  canonicalRouteSpecs.settingsDocuments,
  routePages.DocumentSettingsPage
);
const settingsInventoryRoute = createRoute({
  getParentRoute: () => framedRoute,
  path: canonicalRouteSpecs.settingsInventory.path,
  beforeLoad: ({ context }) => enforceRouteAccess(context, 'member'),
  validateSearch: settingsInventorySearchSchema,
  loaderDeps: ({ search }) => ({ tab: search.tab }),
  loader: ({ context, deps }) => loadInventorySettingsTab(context, deps.tab),
  staticData: { access: 'member' },
  component: createRouteComponent(routePages.InventorySettingsPage),
});

const warehouseDetailsCodec = {
  parse: (params) => (ASCII_INTEGER.test(params.warehouseId) ? { warehouseId: Number(params.warehouseId) } : false),
  stringify: (params) => ({ warehouseId: String(params.warehouseId) }),
} satisfies NonNullable<ParamsOptions<typeof canonicalRouteSpecs.warehouseDetails.path, WarehouseIdParams>['params']>;
const warehouseDetailsRoute = createRoute({
  getParentRoute: () => framedRoute,
  path: canonicalRouteSpecs.warehouseDetails.path,
  beforeLoad: ({ context }) => enforceRouteAccess(context, 'member'),
  params: warehouseDetailsCodec,
  loader: ({ context, params }) => loadWarehouseDetails(context, params.warehouseId),
  staticData: { access: 'member' },
  component: createRouteComponent(routePages.WarehouseDetailsPage),
});
const settingsTeamRoute = createFramedRoute(canonicalRouteSpecs.settingsTeam, routePages.TeamSettingsPage, {
  validateSearch: settingsTeamSearchSchema,
});
settingsTeamRoute.updateLoader({ loader: ({ context }) => loadTeamForwardingBanner(context) });
const documentEditorRoute = createFramedRouteWithProps(
  canonicalRouteSpecs.documentEditor,
  routePages.DocumentEditorPage,
  useDocumentEditorPageProps,
  { validateSearch: documentEditorSearchSchema }
);
const palletTagsRoute = createFramedRoute(canonicalRouteSpecs.palletTags, routePages.PalletTagsPage);
palletTagsRoute.updateLoader({ loader: ({ context }) => loadTagLayouts(context) });
const palletTagEditorRoute = createFramedRouteWithProps(
  canonicalRouteSpecs.palletTagEditor,
  routePages.PalletTagEditorPage,
  usePalletTagEditorPageProps,
  { validateSearch: palletTagEditorSearchSchema }
);
const userProfileRoute = createFramedRoute(canonicalRouteSpecs.userProfile, routePages.UserProfilePage);
const businessIntelligenceRoute = createFramedRoute(
  canonicalRouteSpecs.businessIntelligence,
  routePages.BusinessIntelligencePage
);
/** A search-validation failure here is EXPECTED bad input (a stale `/business-intelligence/<slug>` bookmark carries no `dashboardId`): recover to the overview with a REPLACE so Back does not re-enter the dead URL. Anything else rethrows to the fatal boundary. */
function BIDashboardsErrorComponent({ error }: ErrorComponentProps) {
  const navigate = useNavigate();
  const isInvalidSearch = error instanceof SearchParamError;

  useEffect(() => {
    if (!isInvalidSearch) return;
    void navigate({ replace: true, to: canonicalRouteSpecs.businessIntelligence.path });
  }, [isInvalidSearch, navigate]);

  if (!isInvalidSearch) throw error;
  return null;
}

const biDashboardsRoute = createFramedRouteWithProps(
  canonicalRouteSpecs.biDashboards,
  routePages.BIDashboardsPage,
  useBIDashboardsPageProps,
  { errorComponent: BIDashboardsErrorComponent, validateSearch: biDashboardsSearchSchema }
);
const orderBoardRoute = createFramedRoute(canonicalRouteSpecs.orderBoard, routePages.OrderBoardPage);
orderBoardRoute.updateLoader({ loader: ({ context }) => loadOrderBoardCards(context) });
const scanRoute = createFramedRoute(canonicalRouteSpecs.scan, routePages.ScanPage);
const scanLocationRoute = createFramedRoute(canonicalRouteSpecs.scanLocation, routePages.ScanLocationPage);
const recipeBuilderRoute = createFramedRoute(canonicalRouteSpecs.recipeBuilder, routePages.RecipeBuilderPage, {
  access: 'member-automation',
});

const framedRouteTree = framedRoute.addChildren({
  accountRegisterRoute,
  accountingReportsRoute,
  accountsRoute,
  agingReportsRoute,
  aiInboxRoute,
  chatHomeRoute,
  chatThreadRoute,
  biDashboardsRoute,
  billRoute,
  businessIntelligenceRoute,
  buySellOrderRoute,
  documentEditorRoute,
  documentOnboardingRoute,
  editPickTicketRoute,
  editReceivingTicketRoute,
  growerProductRoute,
  growerProductsRoute,
  inventoryRoute,
  inventoryTransferRoute,
  inventoryTransfersRoute,
  invoicesRoute,
  landingRoute,
  notAMemberRoute,
  orderBoardRoute,
  palletTagEditorRoute,
  palletTagsRoute,
  payablesRoute,
  paymentRoute,
  paymentsRoute,
  pricingSheetRoute,
  printChecksRoute,
  productionRunRoute,
  productionRunsRoute,
  purchaseOrderRoute,
  purchaseOrdersRoute,
  recipeBuilderRoute,
  salesOrderRoute,
  salesOrdersRoute,
  scanLocationRoute,
  scanRoute,
  settingsAccountingRoute,
  settingsAutomationsRoute,
  settingsBusinessRoute,
  settingsDocumentsRoute,
  settingsEmailsRoute,
  settingsEntitiesRoute,
  settingsInventoryRoute,
  settingsOrdersRoute,
  settingsRoute,
  settingsTeamRoute,
  userProfileRoute,
  viewLotTrackingRoute,
  viewPickTicketsRoute,
  viewReceivingTicketsRoute,
  warehouseDetailsRoute,
});

const routeTree = rootRoute.addChildren({
  framedRoute: framedRouteTree,
  loginRoute,
  scanDebugRoute,
  searchResultsRedirectRoute,
});

type CreateAppRouterOptions = Readonly<{ context: AppRouterContext; history: RouterHistory }>;

function createConfiguredAppRouter(context: AppRouterContext, history: RouterHistory) {
  return createRouter({
    context,
    defaultStructuralSharing: true,
    defaultPendingComponent: () => null,
    defaultPendingMinMs: 0,
    defaultPendingMs: 0,
    defaultPreload: 'intent',
    // Apollo owns data freshness (routing/routePreload.ts), so TanStack re-runs a loader on every navigation
    // and never serves a page's earlier loader data (a released query ref) when the page is revisited.
    defaultPreloadStaleTime: 0,
    defaultGcTime: 0,
    defaultErrorComponent: PropagateRouteError,
    caseSensitive: true,
    disableGlobalCatchBoundary: true,
    history,
    notFoundMode: 'root',
    routeTree,
    scrollRestoration: true,
    trailingSlash: 'never',
  });
}

export function createAppRouter({ context, history }: CreateAppRouterOptions) {
  return createConfiguredAppRouter(context, history);
}

export type AppRouter = ReturnType<typeof createAppRouter>;

let browserRouter: AppRouter | undefined;

export function getAppRouter(): AppRouter {
  if (browserRouter !== undefined) return browserRouter;
  if (typeof window === 'undefined') throw new Error('The browser router can only be initialized in a browser.');

  const router = createAppRouter({ context: PENDING_APP_ROUTER_CONTEXT, history: createBrowserHistory({ window }) });

  browserRouter = router;
  return router;
}

declare module '@tanstack/react-router' {
  interface Register {
    router: AppRouter;
  }
}

function useLoginPageProps(): ComponentProps<typeof routePages.LoginPage> {
  const search = loginRoute.useSearch();
  return { redirectTo: search.redirectTo };
}

function usePurchaseOrderPageProps(): ComponentProps<typeof routePages.PurchaseOrderPage> {
  const { slug } = purchaseOrderRoute.useParams();
  return { slug };
}

function useSalesOrderPageProps(): ComponentProps<typeof routePages.SalesOrderPage> {
  const { slug } = salesOrderRoute.useParams();
  return { slug };
}

function useBuySellOrderPageProps(): ComponentProps<typeof routePages.BuySellOrderPage> {
  const { slug } = buySellOrderRoute.useParams();
  return { slug };
}

function useDocumentOnboardingPageProps(): ComponentProps<typeof routePages.DocumentOnboardingPage> {
  const { id } = documentOnboardingRoute.useParams();
  return { id: Number(id) };
}

function useGrowerProductPageProps(): ComponentProps<typeof routePages.GrowerProductPage> {
  const { slug } = growerProductRoute.useParams();
  return { slug };
}

function useAccountingReportsPageProps(): ComponentProps<typeof routePages.AccountingReportsPage> {
  const search = accountingReportsRoute.useSearch();
  return { tab: search.tab };
}

function useBillPageProps(): ComponentProps<typeof routePages.BillPage> {
  const { slug } = billRoute.useParams();
  return { slug };
}

function usePaymentPageProps(): ComponentProps<typeof routePages.PaymentPage> {
  const { slug } = paymentRoute.useParams();
  return { slug };
}

function useAgingReportsPageProps(): ComponentProps<typeof routePages.AgingReportsPage> {
  const search = agingReportsRoute.useSearch();
  return { tab: search.tab };
}

function useProductionRunPageProps(): ComponentProps<typeof routePages.ProductionRunPage> {
  const { slug } = productionRunRoute.useParams();
  return { slug };
}

function usePickTicketPageProps(): ComponentProps<typeof routePages.PickTicketPage> {
  const { slug } = editPickTicketRoute.useParams();
  return { slug };
}

function useReceivingTicketPageProps(): ComponentProps<typeof routePages.ReceivingTicketPage> {
  const { slug } = editReceivingTicketRoute.useParams();
  return { slug };
}

function useLotPageProps(): ComponentProps<typeof routePages.LotPage> {
  const { slug, warehouseId } = viewLotTrackingRoute.useParams();
  return { slug, warehouseId };
}

function useInventoryTransferPageProps(): ComponentProps<typeof routePages.InventoryTransferPage> {
  const { slug } = inventoryTransferRoute.useParams();
  return { slug };
}

function useSettingsEntitiesPageProps(): ComponentProps<typeof routePages.BusinessEntityPage> {
  const { id } = settingsEntitiesRoute.useParams();
  const search = settingsEntitiesRoute.useSearch();
  return { id, ediProductId: search.ediProductId };
}

function useDocumentEditorPageProps(): ComponentProps<typeof routePages.DocumentEditorPage> {
  const search = documentEditorRoute.useSearch();
  return { new: search.new, name: search.name, type: search.type, entity: search.entity, lookup: search.lookup };
}

function usePalletTagEditorPageProps(): ComponentProps<typeof routePages.PalletTagEditorPage> {
  const search = palletTagEditorRoute.useSearch();
  return { name: search.name };
}

// The optional `{-$path}` segment and the `?path=` query are both live inputs;
// the segment wins when a URL carries both.
function useAccountRegisterPageProps(): ComponentProps<typeof routePages.AccountRegisterPage> {
  const { path } = accountRegisterRoute.useParams();
  const search = accountRegisterRoute.useSearch();
  return {
    accountReconciliationId: search.accountReconciliationId,
    initialGridFilters: search.initialGridFilters,
    path: path ?? search.path,
  };
}

// The optional `{-$threadId}` segment and the `?threadId=` query are both live
// inputs; the segment wins when a URL carries both.
function useAiInboxPageProps(): ComponentProps<typeof routePages.AiInboxPage> {
  const { threadId } = aiInboxRoute.useParams();
  const search = aiInboxRoute.useSearch();
  return { threadId: threadId ?? search.threadId };
}

function useBIDashboardsPageProps(): ComponentProps<typeof routePages.BIDashboardsPage> {
  const { slug } = biDashboardsRoute.useParams();
  const search = biDashboardsRoute.useSearch();
  return { slug, dashboardId: search.dashboardId };
}
